const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const MOBILE_QUERY = '(max-width: 749px)';
const CLOCK_MOBILE_QUERY =
  '(max-width: 749px) and (any-hover: none), (max-width: 749px) and (not (any-pointer: fine))';

class FeatureContentMedia extends HTMLElement {
  #observer = null;
  #mediaQuery = null;
  #reducedMotionQuery = null;
  #inView = false;
  #onBreakpointChange = null;
  #onVideoStateChange = () => this.#syncClock();

  connectedCallback() {
    this.#reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    this.#mediaQuery = window.matchMedia(
      this.classList.contains('feature-content__video-clock') ? CLOCK_MOBILE_QUERY : MOBILE_QUERY
    );
    this.#onBreakpointChange = () => {
      requestAnimationFrame(() => this.#syncPlayback());
    };

    this.#observer = new IntersectionObserver(([entry]) => {
      this.#inView = entry.isIntersecting;
      this.#syncPlayback();
    });
    this.#observer.observe(this);
    this.#mediaQuery.addEventListener('change', this.#onBreakpointChange);
    // Media events do not bubble, so listen in the capture phase.
    this.addEventListener('play', this.#onVideoStateChange, true);
    this.addEventListener('pause', this.#onVideoStateChange, true);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#mediaQuery?.removeEventListener('change', this.#onBreakpointChange);
    this.#mediaQuery = null;
    this.#onBreakpointChange = null;
    this.#reducedMotionQuery = null;
    this.removeEventListener('play', this.#onVideoStateChange, true);
    this.removeEventListener('pause', this.#onVideoStateChange, true);
  }

  #syncPlayback() {
    const reducedMotion = this.#reducedMotionQuery?.matches ?? false;
    const players = this.querySelectorAll('video-player-media');

    for (const player of players) {
      const video = player.querySelector('video');
      if (!video) continue;

      const visible = player.offsetParent !== null;
      if (!visible || !this.#inView) {
        video.pause();
        continue;
      }

      if (!reducedMotion && typeof player.playMuted === 'function') {
        player.playMuted();
      }
    }

    this.#syncClock();
  }

  #syncClock() {
    const clock = this.querySelector('feature-content-clock');
    if (!clock) return;

    const visiblePlayer = Array.from(this.querySelectorAll('video-player-media')).find(
      (player) => player.offsetParent !== null
    );
    const video = visiblePlayer?.querySelector('video');
    clock.toggleAttribute('playing', Boolean(this.#inView && video && !video.paused && !video.ended));
  }
}

if (!customElements.get('feature-content-media')) {
  customElements.define('feature-content-media', FeatureContentMedia);
}
